import React, { useEffect, useId, useRef, useState } from 'react';
import { Camera, Image, X } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';
import { optimizeImageForUpload } from '@/utils/imageUpload';

interface CameraCaptureProps {
  /** Función que se ejecuta cuando se selecciona una imagen */
  onImageSelect: (file: File | null) => void;
  /** Imagen actual seleccionada */
  currentImage?: File | null;
  /** Texto del botón para abrir la cámara */
  buttonText?: string;
  /** Clase CSS adicional para el contenedor */
  className?: string;
  /** Si está deshabilitado */
  disabled?: boolean;
}

/**
 * Componente para capturar fotos desde la cámara o seleccionar desde galería
 */
const CameraCapture: React.FC<CameraCaptureProps> = ({
  onImageSelect,
  currentImage,
  buttonText = 'Añadir Foto',
  className = '',
  disabled = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputId = useId();
  const cameraInputId = useId();
  const hiddenInputClassName = 'absolute h-px w-px overflow-hidden opacity-0 pointer-events-none';

  /**
   * Maneja la selección de archivo desde galería
   */
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void processSelectedFile(file);
    }
  };

  /**
   * Maneja la captura desde cámara
   */
  const handleCameraCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void processSelectedFile(file);
    }
  };

  const resetInputValues = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  /**
   * Procesa el archivo seleccionado y crea la preview
   */
  const processSelectedFile = async (file: File) => {
    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido');
      return;
    }

    const optimizedFile = await optimizeImageForUpload(file);

    // Validar tamaño (máximo 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (optimizedFile.size > maxSize) {
      alert('La imagen es demasiado grande. El tamaño máximo es 10MB');
      return;
    }

    // Crear URL de preview
    const url = URL.createObjectURL(optimizedFile);
    setPreviewUrl(url);
    onImageSelect(optimizedFile);
    setIsModalOpen(false);
    resetInputValues();
  };

  /**
   * Elimina la imagen seleccionada
   */
  const removeImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    onImageSelect(null);
  };

  /**
   * Cierra el modal
   */
  const closeModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!currentImage) {
      setPreviewUrl((currentPreviewUrl) => {
        if (currentPreviewUrl) {
          URL.revokeObjectURL(currentPreviewUrl);
        }
        return null;
      });
      return;
    }

    const url = URL.createObjectURL(currentImage);
    setPreviewUrl((currentPreviewUrl) => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }
      return url;
    });

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [currentImage]);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Inputs ocultos para archivo y cámara */}
      <input
        id={galleryInputId}
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className={hiddenInputClassName}
      />
      <input
        id={cameraInputId}
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
        className={hiddenInputClassName}
      />

      {/* Preview de la imagen */}
      {previewUrl ? (
        <div className="relative">
          <img
            src={previewUrl}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
          />
          <button
            onClick={removeImage}
            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
            disabled={disabled}
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        /* Botón para añadir foto */
        <Button
          type="button"
          onClick={() => setIsModalOpen(true)}
          variant="outline"
          className="w-full h-48 border-2 border-dashed border-gray-300 hover:border-gray-400 flex flex-col items-center justify-center space-y-2"
          disabled={disabled}
        >
          <Camera size={32} className="text-gray-400" />
          <span className="text-gray-500">{buttonText}</span>
        </Button>
      )}

      {/* Modal para seleccionar origen */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
      >
        <div className="space-y-4">
          {/* Título del modal */}
          <h3 className="text-lg font-semibold text-center text-white">
            Seleccionar Imagen
          </h3>
          
          <p className="text-gray-400 text-center">
            ¿Cómo quieres añadir la imagen?
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <label
              htmlFor={cameraInputId}
              onClick={resetInputValues}
              className="font-semibold rounded-xl transition-all duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-emerald-500/30 transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden flex h-24 cursor-pointer items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-3 text-base text-white shadow-lg hover:from-emerald-600 hover:to-emerald-700 hover:shadow-emerald-500/25 hover:shadow-xl"
            >
              <Camera size={24} />
              <span>Cámara</span>
            </label>
            
            <label
              htmlFor={galleryInputId}
              onClick={resetInputValues}
              className="font-semibold rounded-xl transition-all duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-emerald-500/30 transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden flex h-24 cursor-pointer items-center justify-center gap-2 border-2 border-emerald-500 px-6 py-3 text-base text-emerald-500 hover:bg-emerald-500 hover:text-white hover:shadow-emerald-500/25 hover:shadow-xl"
            >
              <Image size={24} />
              <span>Galería</span>
            </label>
          </div>
          
          <Button
            type="button"
            onClick={closeModal}
            variant="outline"
            className="w-full"
          >
            Cancelar
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default CameraCapture;
